<template>
  <div class="customer-orders">
    <div class="orders-layout">
      <!-- Status sidebar -->
      <div class="status-sidebar">
        <ul class="status-list">
          <li
            v-for="(status, index) in orderStatusOptions"
            :key="index"
            class="status-item noselect"
            :class="{
              selected: status.value === filters.statusShipment,
              alertLine: status.alertOnPositive,
              warningLine: status.warningLine
            }"
            @click="updateStatusFilter(status.value)"
          >
            {{ status.displayText }}
          </li>
        </ul>
      </div>

      <!-- Orders content -->
      <div class="orders-content">
        <div class="orders-header">
          <div class="flex items-center gap-2">
            <BaseMiniSpinner v-if="loading" />
            <span class="font-bold">Orders ({{ totalItems }})</span>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="text"
              v-model="filters.search"
              class="search-input"
              placeholder="Search orders..."
              @keyup.enter="applyFilters"
            />
            <input
              type="text"
              v-model="filters.otherFilters.nkrCode"
              class="search-input"
              placeholder="Search by NKR code..."
              @keyup.enter="applyFilters"
            />
            <button class="btn-info-button" @click="applyFilters">
              Search
            </button>
          </div>
        </div>

        <div class="orders-table-wrapper">
          <table class="default-table">
            <thead>
              <tr class="table100-head">
                <th @click="changeArrowDir('orderNumber')">
                  Order #
                  <span v-if="filters.orderCol === 'orderNumber'">
                    <img
                      :src="
                        require(`@/assets/icons/${arrowDirection}-arrow.svg`)
                      "
                    />
                  </span>
                </th>
                <th @click="changeArrowDir('orderDate')">
                  Date
                  <span v-if="filters.orderCol === 'orderDate'">
                    <img
                      :src="
                        require(`@/assets/icons/${arrowDirection}-arrow.svg`)
                      "
                    />
                  </span>
                </th>
                <th>Status</th>
                <th>Products</th>
                <th @click="changeArrowDir('total')">
                  Total
                  <span v-if="filters.orderCol === 'total'">
                    <img
                      :src="
                        require(`@/assets/icons/${arrowDirection}-arrow.svg`)
                      "
                    />
                  </span>
                </th>
                <th>Shipping Name</th>
                <th>Web user</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in orders"
                :key="order.orderNumber"
                @click="$emit('openOrder', order.orderNumber)"
                class="cursor-pointer"
                :class="{ processed: order.dropshipProcessed == 1 }"
              >
                <td>
                  <div class="flex items-center gap-2">
                    <a>{{ order.orderNumber }}</a>
                    <div
                      v-if="order.statusPayment == 0"
                      class="payment-warning-badge"
                      title="Not paid"
                    >
                      <i class="mdi mdi-alert-circle"></i>
                    </div>
                  </div>
                </td>
                <td>{{ order.orderDate | dateWithHours }}</td>
                <td>{{ order.status }}</td>
                <td>
                  <BaseTooltip
                    :text="order.products"
                    :disabled="!order.products || order.products === ''"
                  >
                    {{ order.products }}
                  </BaseTooltip>
                </td>
                <td>{{ currency }}{{ order.total }}</td>
                <td>
                  <BaseTooltip
                    :text="order.shippingAddressName"
                    :disabled="
                      !order.shippingAddressName ||
                        order.shippingAddressName === ''
                    "
                  >
                    {{ order.shippingAddressName }}
                  </BaseTooltip>
                </td>
                <td>
                  {{ order.customerEmail }}
                  <span
                    v-if="!order.customerInfoautoId"
                    class="not-billed-badge"
                    title="Placed by a linked web user, not billed to this infoauto customer"
                  >
                    Not billed
                  </span>
                </td>
                <td>{{ order.createdBy }}</td>
              </tr>
              <tr v-if="!loading && orders.length === 0">
                <td colspan="8" class="text-center py-4 text-gray-500">
                  No orders found
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards">
          <div
            v-for="order in orders"
            :key="order.orderNumber"
            @click="$emit('openOrder', order.orderNumber)"
            class="order-card"
            :class="{
              warning: order.statusPayment == 0,
              processed: order.dropshipProcessed == 1
            }"
          >
            <div class="card-header">
              <div class="order-number">
                <i class="mdi mdi-receipt text-blue-600"></i>
                <span class="font-bold">{{ order.orderNumber }}</span>
              </div>
              <div class="order-total">
                <span class="font-bold text-green-600"
                  >{{ currency }}{{ order.total }}</span
                >
              </div>
            </div>

            <div class="card-content">
              <div class="info-row">
                <i class="mdi mdi-calendar text-gray-500"></i>
                <span>{{ order.orderDate | dateWithHours }}</span>
              </div>

              <div class="info-row" v-if="order.status">
                <i class="mdi mdi-information text-gray-500"></i>
                <span>Status: {{ order.status }}</span>
              </div>

              <div class="info-row" v-if="order.products">
                <i class="mdi mdi-package-variant text-gray-500"></i>
                <span class="truncate">{{ order.products }}</span>
              </div>

              <div class="info-row" v-if="order.shippingAddressName">
                <i class="mdi mdi-account text-gray-500"></i>
                <span class="truncate">{{ order.shippingAddressName }}</span>
              </div>

              <div class="info-row" v-if="order.customerEmail">
                <i class="mdi mdi-email-outline text-gray-500"></i>
                <span class="truncate">{{ order.customerEmail }}</span>
                <span v-if="!order.customerInfoautoId" class="not-billed-badge">
                  Not billed
                </span>
              </div>

              <div class="info-row" v-if="order.createdBy">
                <i class="mdi mdi-account-circle text-gray-500"></i>
                <span>Created by: {{ order.createdBy }}</span>
              </div>
            </div>

            <div class="card-footer">
              <div class="status-indicators">
                <div
                  v-if="order.statusPayment == 0"
                  class="payment-warning"
                  title="Not paid"
                >
                  <i class="mdi mdi-alert-circle text-orange-500"></i>
                </div>
                <div
                  v-if="order.dropshipProcessed == 1"
                  class="processed-indicator"
                  title="Processed"
                >
                  <i class="mdi mdi-check-circle text-green-600"></i>
                </div>
              </div>
              <div class="card-action">
                <i class="mdi mdi-chevron-right text-gray-400"></i>
              </div>
            </div>
          </div>

          <div
            v-if="!loading && orders.length === 0"
            class="text-center py-4 text-gray-500"
          >
            No orders found
          </div>
        </div>

        <!-- Pagination -->
        <div class="orders-footer" v-if="totalItems > 0">
          <div class="totals">
            {{ (filters.page - 1) * filters.limitPerPage + 1 }} -
            {{ toQuantity() }} of {{ totalItems }}
          </div>
          <div class="pages">
            <a class="page-link" @click="previous">&laquo; Previous</a>
            <div class="mx-2">{{ filters.page }} / {{ totalPages }}</div>
            <a class="page-link" @click="next">Next &raquo;</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOrders } from "@/api/store/OrdersService.js";

export default {
  name: "InfoautoCustomerOrders",
  props: {
    customerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      orders: [],
      totalItems: 0,
      totalPages: 0,
      loading: false,
      apiCallId: null,
      filters: {
        statusShipment: "",
        otherFilters: {
          createdBy: "",
          seller: "",
          fromDate: "",
          toDate: "",
          paidStatus: "",
          creditRetail: "",
          hideCancelled: false,
          dropshipProcessed: 0,
          customerInfoautoId: "",
          // Also orders placed by linked web users that were not billed
          includeLinkedWebCustomers: true,
          nkrCode: ""
        },
        page: 1,
        limitPerPage: 50,
        orderCol: "orderDate",
        orderDir: "desc",
        search: ""
      }
    };
  },
  computed: {
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    arrowDirection() {
      return this.filters.orderDir === "desc" ? "down" : "up";
    },
    orderStatusOptions() {
      return [
        { displayText: "All", value: "" },
        { displayText: "Awaiting Shipment", value: "awaiting_shipment" },
        { displayText: "Awaiting Payment", value: "awaiting_payment" },
        { displayText: "Shipped", value: "shipped" },
        {
          displayText: "Cancel Request Pending",
          value: "Cancel request pending",
          alertOnPositive: true
        },
        { displayText: "Cancelled", value: "cancelled" },
        { displayText: "On Hold", value: "on_hold" },
        {
          displayText: "Local Dropship",
          value: "Local Dropship",
          warningLine: true
        },
        {
          displayText: "Dropship Pending",
          value: "Dropship Pending",
          warningLine: true
        },
        { displayText: "Dropship Processed", value: "Dropship processed" },
        { displayText: "VIN Verification", value: "VIN_verification" },
        { displayText: "Processing", value: "processing" },
        { displayText: "Draft", value: "draft" },
        {
          displayText: "Warehouse Allocation",
          value: "warehouse_allocation"
        }
      ];
    }
  },
  watch: {
    customerId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.filters.otherFilters.customerInfoautoId = val;
          this.filters.page = 1;
          this.loadOrders();
        }
      }
    }
  },
  methods: {
    updateStatusFilter(status) {
      this.filters.statusShipment = status;
      this.filters.page = 1;
      if (status === "cancelled") {
        this.filters.otherFilters.hideCancelled = false;
      } else {
        this.filters.otherFilters.hideCancelled = false;
      }
      this.loadOrders();
    },
    applyFilters() {
      this.filters.page = 1;
      this.loadOrders();
    },
    changeArrowDir(headerValue) {
      this.filters.page = 1;
      if (headerValue === this.filters.orderCol) {
        this.filters.orderDir =
          this.filters.orderDir === "desc" ? "asc" : "desc";
      } else {
        this.filters.orderCol = headerValue;
        this.filters.orderDir = "desc";
      }
      this.loadOrders();
    },
    async loadOrders() {
      this.loading = true;
      this.apiCallId = new Date();
      const apiCallIdCurrent = this.apiCallId;

      try {
        const res = await getOrders(this.filters);
        if (apiCallIdCurrent !== this.apiCallId) return;
        this.orders = res.orders;
        this.totalItems = res.totalItems;
        this.totalPages = res.totalPages;
      } catch {
        this.orders = [];
        this.totalItems = 0;
        this.totalPages = 0;
      } finally {
        if (apiCallIdCurrent === this.apiCallId) {
          this.loading = false;
        }
      }
    },
    previous() {
      if (this.filters.page > 1) {
        this.filters.page--;
        this.loadOrders();
      }
    },
    next() {
      if (this.filters.page < this.totalPages) {
        this.filters.page++;
        this.loadOrders();
      }
    },
    toQuantity() {
      const end = this.filters.limitPerPage * this.filters.page;
      return end > this.totalItems ? this.totalItems : end;
    }
  }
};
</script>

<style lang="scss" scoped>
.not-billed-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  background: #fef3c7;
  color: #92400e;
  white-space: nowrap;
}

.customer-orders {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.orders-layout {
  display: flex;
  height: 100%;
  gap: 10px;
}

.status-sidebar {
  width: 180px;
  min-width: 180px;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
  overflow-y: auto;

  .status-list {
    list-style: none;
    padding: 0;
    margin: 0;

    .status-item {
      padding: 8px 12px;
      cursor: pointer;
      font-size: 13px;
      border-bottom: 1px solid #f3f4f6;
      transition: background-color 0.15s;

      &:hover {
        background-color: #f3f4f6;
      }

      &.selected {
        background-color: #3b82f6;
        color: white;
        font-weight: 600;
      }

      &.alertLine {
        color: #dc2626;
        font-weight: 600;
      }

      &.alertLine.selected {
        background-color: #dc2626;
        color: white;
      }

      &.warningLine {
        color: #d97706;
        font-weight: 600;
      }

      &.warningLine.selected {
        background-color: #d97706;
        color: white;
      }
    }
  }
}

.orders-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.orders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  flex-shrink: 0;

  .search-input {
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    width: 200px;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
    }
  }
}

.orders-table-wrapper {
  flex: 1;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 5px;

  table {
    width: 100%;

    th {
      cursor: pointer;
      white-space: nowrap;
      font-size: 13px;

      img {
        width: 10px;
        display: inline;
      }
    }

    td {
      font-size: 13px;
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    tbody tr:hover {
      background-color: #dbeafe;
    }

    .processed {
      background-color: #d1fae5;
    }
  }
}

.orders-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 13px;
  flex-shrink: 0;

  .pages {
    display: flex;
    align-items: center;

    .page-link {
      cursor: pointer;
      color: #3b82f6;
      padding: 4px 8px;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.payment-warning-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #f59e0b;
  border-radius: 50%;
  color: white;
  flex-shrink: 0;

  i {
    font-size: 14px;
    line-height: 1;
  }
}

.cursor-pointer {
  cursor: pointer;
}

/* En móvil apilamos el sidebar de estados sobre el contenido y lo
   convertimos en una tira horizontal scrollable para no robar ancho
   a la tabla */
@media (max-width: 640px) {
  .orders-layout {
    flex-direction: column;
    gap: 8px;
  }

  .status-sidebar {
    width: 100%;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;

    .status-list {
      display: flex;
      flex-wrap: nowrap;

      .status-item {
        white-space: nowrap;
        border-bottom: none;
        border-right: 1px solid #f3f4f6;
      }
    }
  }

  .orders-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    .search-input {
      width: 100%;
      flex: 1;
    }
  }

  /* En móvil ocultamos la tabla y mostramos tarjetas, igual que en
     OrdersPage, para no comprimir 7 columnas en pantalla angosta */
  .orders-table-wrapper {
    display: none;
  }

  .mobile-cards {
    display: block;
  }
}

/* Mobile Card View */
.mobile-cards {
  display: none;

  .order-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover {
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
    }

    &.warning {
      border-left: 4px solid #f59e0b;
      background-color: #fffbeb;
    }

    &.processed {
      border-left: 4px solid #10b981;
      background-color: #f0fdf4;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #f3f4f6;

      .order-number {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;

        i {
          font-size: 18px;
          flex-shrink: 0;
        }

        span {
          font-size: 15px;
        }
      }

      .order-total {
        font-size: 15px;
        font-weight: bold;
      }
    }

    .card-content {
      .info-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
        font-size: 13px;
        color: #374151;

        i {
          font-size: 16px;
          width: 16px;
          flex-shrink: 0;
        }

        span {
          flex: 1;
          min-width: 0;

          &.truncate {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #f3f4f6;

      .status-indicators {
        display: flex;
        gap: 8px;

        i {
          font-size: 16px;
        }
      }

      .card-action i {
        font-size: 20px;
      }
    }
  }
}
</style>
